import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AssetMeasurement } from '../../../model/asset-measurement';
import { Asset } from '../../../model/asset';
import { AssetProperty } from '../../../model/asset-property';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AssetsService } from '../../../services/assets.service';
import { AssetPersistRequest } from '../../../model/requests/asset-persist-request';
import { ConfirmationService } from 'primeng/api';
import { CommonMessagesService } from '../../../services/common-messages.service';
import { MapService } from '../../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import { AssetsFormInfoComponent } from './assets-form-info/assets-form-info.component';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import {TemplatesService} from "../../../services/templates.service";
import {Template} from "../../../model/template";
import {DefaultAssetRepresentation} from "../../../model/default-asset-representation";

@Component({
  selector: 'app-assets-form',
  templateUrl: './assets-form.component.html',
  styleUrls: ['./assets-form.component.scss']
})
export class AssetsFormComponent implements OnInit, ComponentCanDeactivate {

  public readonly DEFAULT_ZOOM = 14;
  public readonly DEFAULT_LATITUDE = 39.984255;
  public readonly DEFAULT_LONGITUDE = -0.027077;

  @Input() active: boolean;
  @Output() activeChange = new EventEmitter();

  items: any[];
  currentStep: number;
  stepChanged: boolean;

  asset: Asset;
  template: Template;
  defaultAssetRepresentation: DefaultAssetRepresentation;
  previousTemplateUuid: string;
  assetMeasurements: AssetMeasurement[];
  assetProperties: AssetProperty[];
  persistingChanges: boolean;
  formLoaded: boolean;
  formSuccess: boolean;

  map: mapboxgl.Map;

  mode: 'create' | 'edit';

  @ViewChild(AssetsFormInfoComponent)
  assetsFormInfo: AssetsFormInfoComponent;

  constructor(private assetsService: AssetsService,
              private templatesService: TemplatesService,
              private route: ActivatedRoute,
              private router: Router,
              private mapService: MapService,
              private messageService: MessageService,
              private confirmationService: ConfirmationService,
              private commonMessagesService: CommonMessagesService) {
    this.asset = new Asset();
    this.assetMeasurements = [];
    this.assetProperties = [];
    this.stepChanged = false;
  }

  ngOnInit(): void {
    this.currentStep = 0;

    this.items = [
      {index: 0, icon: 'pi pi-map-marker', label: 'model.asset', description: 'forms.descriptions.asset'},
      {index: 1, icon: 'pi pi-wifi', label: 'model.sensors', description: 'forms.descriptions.assetSensors'},
      {index: 2, icon: 'pi pi-bars', label: 'model.properties', description: 'forms.descriptions.assetProperties'},
      {index: 3, icon: 'pi pi-check', label: 'forms.confirmation'}
    ];

    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  buildAndConfigureMap(): void {
    const latitude = this.asset.latitude ? this.asset.latitude : this.DEFAULT_LATITUDE;
    const longitude = this.asset.longitude ? this.asset.longitude : this.DEFAULT_LONGITUDE;
    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, latitude, longitude, 'confirmationMap');
    this.mapService.buildStaticPulsingDot(this.map, latitude, longitude);
  }

  loadCreationForm(): void {
    this.asset = new Asset();
    this.formLoaded = true;
  }

  loadEditionForm(uuid): void {
    const promises = [
      this.assetsService.getAsset(uuid).toPromise()
        .then(response => {
          this.asset = response.asset;
          if (this.asset.technicalDetails) {
            this.asset.technicalDetails = JSON.parse(this.asset.technicalDetails);
          }
        }, error => {
          if (error.status === 404) {
            this.router.navigate(['/not-found']);
          }
        }),
      this.assetsService.getAssetMeasurementsOfAsset(uuid).toPromise()
        .then(response => this.assetMeasurements = response.assetMeasurements),
      this.assetsService.getAssetProperties(uuid).toPromise()
        .then(response => this.assetProperties = response.assetProperties)];

    Promise.all(promises).then(() => this.formLoaded = true);
  }

  persistAssetData(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.assetsService.createAsset(persistData).toPromise();
    } else {
      promise = this.assetsService.editAsset(persistData).toPromise();
    }

    promise.then(response => {
      if (this.active) {
        this.active = false;
        this.activeChange.emit(this.active);
      }
      this.formSuccess = true;
      this.router.navigate(([`/assets/${this.mode === 'create' ? response.uuid : persistData.uuid}/details`]));
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.alias);
      this.persistingChanges = false;
    });
  }

  nextStep(): void {
    this.stepChanged = true;
    if (this.currentStep === 0 && this.template && (!this.previousTemplateUuid || this.previousTemplateUuid != this.template.uuid)) {
      this.templateChanged(this.template);
    } else {
      if (this.currentStep < this.items.length - 1) {
        this.currentStep++;
      }
      if (this.currentStep === this.items.length - 1) {
        setTimeout(() => this.buildAndConfigureMap(), 200);
      }
    }
  }

  previousStep(): void {
    this.stepChanged = true;
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  tenantChanged(): void {
    this.assetMeasurements = [];
    this.assetProperties = [];
  }

  templateChanged(template: Template): void {
    this.templatesService.getTemplate(template.uuid).toPromise().then(response => {
      let fullTemplate = response.template;
      if (fullTemplate.content?.assetMeasurements) {
        this.assetMeasurements.push.apply(this.assetMeasurements, fullTemplate.content.assetMeasurements);
      }
      if (fullTemplate.content?.assetProperties) {
        this.assetProperties.push.apply(this.assetProperties, fullTemplate.content.assetProperties);
      }
      this.previousTemplateUuid = this.template.uuid;
      this.currentStep++;
    }, error => {
      if (error.status === 404) {
        this.router.navigate(['/not-found']);
      }
    });
  }

  private buildPersistData(): AssetPersistRequest {
    return {
      alias: this.asset.alias,
      description: this.asset.description,
      latitude: this.asset.latitude,
      longitude: this.asset.longitude,
      timezone: this.asset.timezone,
      technicalDetails: !this.asset.technicalDetails ? null : JSON.stringify(this.asset.technicalDetails),
      tenantUuid: this.asset.tenant.uuid,
      organizationUuid: this.asset.organization.uuid,
      assetRepresentation: this.asset.assetRepresentation ? this.asset.assetRepresentation : this.defaultAssetRepresentation,
      assetMeasurements: this.assetMeasurements,
      assetProperties: this.assetProperties,
      uuid: this.mode === 'edit' ? this.asset.uuid : null
    };
  }

  cancelAction(): void {
    this.router.navigate(([`/assets`]));
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    if (this.formSuccess) {
      return true;
    }
    return !this.stepChanged && (this.currentStep === 0 && this.assetsFormInfo.assetForm.untouched);
  }

}
